import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, BarChart3, AlertOctagon, CalendarRange, Share2, Check, Copy } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SiteFooter from '@/components/SiteFooter';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const LEVEL_COLORS = {
  low: 'text-emerald-600',
  medium: 'text-amber-600',
  high: 'text-orange-600',
  extreme: 'text-rose-600',
};

function fmtWeek(weekId) {
  try {
    const d = new Date(`${weekId}T12:00:00Z`);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return weekId;
  }
}

function buildShareText(report) {
  const top = report.busiest_hubs?.[0];
  const lines = [report.title];
  if (top) lines.push(`✈️ Busiest hub: ${top.code} (${top.city}) — ${(top.crowd_level || '').toUpperCase()}`);
  if (report.outlook) lines.push(`📅 Week ahead: busiest ${report.outlook.busiest_day} · quietest ${report.outlook.quietest_day}`);
  if (report.stats?.hubs_delayed_at_snapshot > 0) lines.push(`⚠️ ${report.stats.hubs_delayed_at_snapshot} of ${report.stats.hubs_tracked} hubs under FAA delays`);
  lines.push(`Full report: https://howbusyismyairport.com/reports/${report.week_id}`);
  return lines.join('\n\n');
}

function ShareCard({ report }) {
  const [copied, setCopied] = useState(false);
  const text = buildShareText(report);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <Card className="border-slate-200 bg-slate-900 text-slate-100 mb-6" data-testid="report-share-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Share2 className="w-4 h-4 text-sky-400" />
          <h3 className="heading font-semibold text-white">Share this report</h3>
        </div>
        <pre className="text-xs bg-slate-800 rounded-lg p-3 whitespace-pre-wrap mb-3 text-slate-200" data-testid="report-share-text">{text}</pre>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="report-copy-btn"
            onClick={copy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
          >
            {copied ? <><Check className="w-4 h-4 text-emerald-400" /> Copied</> : <><Copy className="w-4 h-4" /> Copy text</>}
          </button>
          <a
            data-testid="report-tweet-btn"
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-sm font-medium text-white transition-colors"
          >
            Post on X
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

export default function WeeklyReportPage() {
  const { weekId } = useParams();
  const [report, setReport] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const url = weekId ? `${API}/reports/${weekId}` : `${API}/reports/latest`;
        const [r, a] = await Promise.all([fetch(url), fetch(`${API}/reports`)]);
        if (!cancel) {
          if (r.ok) setReport(await r.json());
          if (a.ok) setArchive((await a.json()).reports || []);
        }
      } catch { /* static content still renders */ }
      if (!cancel) setLoading(false);
    })();
    return () => { cancel = true; };
  }, [weekId]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="weekly-report-page">
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <span className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Predictor
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 mb-2">
              Weekly Series · Published every week
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              US Airport Crowd Report
            </h1>
            <p className="text-lg text-slate-600">
              A weekly, data-generated snapshot of American air travel: which hubs are running
              busiest, where FAA delays are hitting, the most disrupted airports, and the
              crowd outlook for the week ahead — straight from the live flight data and
              prediction model behind this site.
            </p>
          </div>

          {loading && <p className="text-sm text-slate-500 py-8 text-center">Loading this week's report…</p>}

          {!loading && report && (
            <article data-testid="weekly-report-body">
              <h2 className="text-2xl font-semibold text-slate-900 mb-1">{report.title}</h2>
              <p className="text-xs text-slate-400 mb-6">
                Generated {new Date(report.generated_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                {' '}· {report.stats?.hubs_tracked} hubs tracked · {report.stats?.hubs_delayed_at_snapshot} with active FAA delays at snapshot
              </p>

              {/* Outlook */}
              {report.outlook && (
                <Card className="border-slate-200 bg-white mb-6" data-testid="report-outlook">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <CalendarRange className="w-4 h-4 text-blue-500" />
                      <h3 className="heading font-semibold text-slate-900">The week ahead</h3>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">
                      Busiest day to fly: <strong className="text-rose-600">{report.outlook.busiest_day}</strong> ·
                      Quietest: <strong className="text-emerald-600">{report.outlook.quietest_day}</strong>
                    </p>
                    <div className="flex items-end gap-1.5 h-16">
                      {(report.outlook.days || []).map((d) => {
                        const max = Math.max(...report.outlook.days.map((x) => x.avg_score));
                        const pct = max ? Math.max(12, (d.avg_score / max) * 100) : 50;
                        const busy = d.day === report.outlook.busiest_day;
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                            <div
                              className={`w-full rounded-t ${busy ? 'bg-rose-400' : 'bg-blue-300'}`}
                              style={{ height: `${pct}%` }}
                              title={`${d.day}: ${d.avg_score}`}
                            />
                            <span className="text-[10px] text-slate-400">{d.day.slice(0, 3)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Busiest hubs */}
              <Card className="border-slate-200 bg-white mb-6" data-testid="report-busiest">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-blue-500" />
                    <h3 className="heading font-semibold text-slate-900">Busiest hubs this week</h3>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {(report.busiest_hubs || []).map((h, i) => (
                      <li key={h.code} className="flex items-center justify-between py-2 text-sm">
                        <span className="flex items-center gap-3 min-w-0">
                          <span className="text-[11px] font-bold text-slate-300 w-5 text-right">#{i + 1}</span>
                          <Link to={`/airports/${(h.code || '').toLowerCase()}`} className="font-mono font-semibold text-blue-600 hover:underline">
                            {h.code}
                          </Link>
                          <span className="text-slate-500 truncate">{h.city}</span>
                        </span>
                        <span className="flex items-center gap-3 whitespace-nowrap">
                          {h.delay_color && h.delay_color !== 'green' && (
                            <span className="text-[11px] text-rose-500">FAA delay</span>
                          )}
                          <span className={`text-xs font-semibold uppercase ${LEVEL_COLORS[h.crowd_level] || 'text-slate-500'}`}>
                            {h.crowd_level}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Most disrupted */}
              {report.most_disrupted?.length > 0 && (
                <Card className="border-slate-200 bg-white mb-6" data-testid="report-disrupted">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertOctagon className="w-4 h-4 text-rose-500" />
                      <h3 className="heading font-semibold text-slate-900">Most disrupted at snapshot</h3>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {report.most_disrupted.map((d) => (
                        <li key={d.code || d.name} className="flex items-center justify-between py-2 text-sm">
                          <span className="flex items-center gap-3">
                            <span className="font-mono font-semibold text-slate-900">{d.code}</span>
                            <span className="text-slate-500 truncate">{d.name}</span>
                          </span>
                          <span className="text-xs whitespace-nowrap">
                            <span className="text-amber-600 font-medium">{d.delays} delayed</span>
                            {' · '}
                            <span className="text-rose-600 font-medium">{d.cancellations} cancelled</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <ShareCard report={report} />
            </article>
          )}

          {!loading && !report && (
            <p className="text-sm text-slate-500 py-8 text-center">
              This report isn't available. <Link to="/reports" className="text-blue-600 underline">See the latest report</Link>.
            </p>
          )}

          {/* Archive */}
          {archive.length > 0 && (
            <Card className="border-slate-200 bg-white mb-10" data-testid="report-archive">
              <CardContent className="p-5">
                <h3 className="heading font-semibold text-slate-900 mb-3">Report archive</h3>
                <ul className="space-y-1.5">
                  {archive.map((r) => (
                    <li key={r.week_id}>
                      <Link to={`/reports/${r.week_id}`} className="text-sm text-blue-600 hover:underline">
                        Week of {fmtWeek(r.week_id)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Static editorial — prerendered for crawlers */}
          <section className="space-y-4 text-slate-700 leading-relaxed text-sm">
            <h2 className="text-xl font-semibold text-slate-900">About this series</h2>
            <p>
              The US Airport Crowd Report is generated weekly from the same data pipeline that powers
              our <Link to="/" className="text-blue-600 underline">live crowd predictor</Link>: scheduled
              departure volumes from FlightAware for 16 major US hubs, FAA-reported delay programs, and
              worldwide disruption counts. The "week ahead" outlook uses our deterministic scoring model —
              flight-density weighted by aircraft capacity and time-of-day patterns — the same math
              explained on our <Link to="/methodology" className="text-blue-600 underline">methodology page</Link>.
            </p>
            <p>
              Rankings reflect conditions at the moment each report is generated and typical weekly
              patterns; day-of-travel conditions can differ. For a specific flight, always check the{' '}
              <Link to="/live" className="text-blue-600 underline">live dashboard</Link> or run a{' '}
              <Link to="/" className="text-blue-600 underline">prediction</Link> for your departure hour.
              Reports are free to cite with attribution to HowBusyIsMyAirport.com — journalists can{' '}
              <Link to="/contact" className="text-blue-600 underline">contact us</Link> for custom data.
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
